import React from "react";
import Bugsnag from "@bugsnag/js";
import * as Yup from "yup";
import { Field, FieldProps, Form, Formik } from "formik";
import { CountryList } from "../../../../assets/staticData/CountryList";
import { useBuyingProcess } from "../../../../services/buyhook";
import { ErrorMessages } from "../../../../services/errorMessages";
import DropSelectSearch from "../../../../ui-elements/DropSelect/DropSelectSearch/DropSelectSearch";
import Input from "../../../../ui-elements/Input/Input";
import Button from "../../../../ui-elements/Button/Button";
import Tooltip from "../../../../ui-elements/Tooltip/Tooltip";
import { ForeignCompanyPepReport } from "../../../../types/CompanyAnswersTypes";

interface IProps {
    changeSearchResults: (report: ForeignCompanyPepReport | null | undefined) => void;
    isDisabled: boolean;
    initialValues?: ISubFormValues;
    riskscoreId: string | null;
}

interface ISubFormValues {
    country_code: string;
    name: string;
}

const AlthjodlegirValistarForm: React.FC<IProps> = ({
    isDisabled,
    changeSearchResults,
    initialValues,
    riskscoreId
}) => {
    const { tryPurchaseItem, tryToBuyItem, resetState, ModalsAndToasts } = useBuyingProcess(
        async (values: ISubFormValues, reference: string) => {
            // Reset search results to null to show loading state while fetching
            changeSearchResults(null);

            try {
                // Build the request body matching the C# ForeignCompanyPepInput model
                const requestBody = {
                    companyName: values.name,
                    country: values.country_code || null, // Send null if empty, matching the nullable Country property
                    matchRate: 85, // Default match rate as specified in the C# GetModelAsHttpQueryString method
                    maxResults: 10, // You can make this configurable if needed
                    reference: reference || null, // Include reference for receipt when charging
                };

                // Build the URL with optional riskscoreId query parameter
                // This allows the backend to associate the search with a specific risk assessment
                let url = `/Areidanleikakannanir/Erlend-Skyrsla-KYC-Logadilar-Kaupa`;
                if (riskscoreId !== null && riskscoreId !== undefined) {
                    url += `?riskscoreId=${riskscoreId}`;
                }

                // Make POST request with JSON body instead of form-urlencoded
                const response = await fetch(url, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(requestBody),
                });

                // Handle non-OK responses by showing error modal
                if (!response.ok) {
                    // Reset loading state by setting searchResults back to undefined
                    changeSearchResults(undefined);
                    //Get error message from response
                    const errorMessage = await response.text();
                    console.error("Error response from server:", errorMessage);
                    resetState({
                        headText: ErrorMessages.RequestFailed,
                        type: "alert",
                    });
                    return;
                }

                // Handle redirects (e.g., if user needs to authenticate or authorize)
                if (response.redirected) {
                    window.location.href = response.url;
                    return;
                }
                if (response.ok) {
                    const responseBody: ForeignCompanyPepReport = await response.json();
                    changeSearchResults(responseBody);
                    return;
                }
                // Handle specific error cases based on backend response message
                // Reset loading state before showing error
                changeSearchResults(undefined);
                resetState({
                    headText: ErrorMessages.Other,
                    type: "error",
                });
            } catch (e) {
                // Log unexpected errors to Bugsnag for monitoring
                // Reset loading state before showing error
                changeSearchResults(undefined);
                if (e instanceof Error) Bugsnag.notify(e);
                resetState({ headText: ErrorMessages.NetworkError, type: "error" });
            }
        },
        "alþjóðlegum válistum"
    );

    /**
     * Renders the form using Formik for validation and state management
     * The form is wrapped in either a normal div or a Tooltip depending on isDisabled prop
     */
    const renderForm = () => (
        <Formik
            // enableReinitialize allows the form to update when initialValues prop changes
            enableReinitialize
            initialValues={{
                country_code: initialValues?.country_code || "",
                name: initialValues?.name || "",
            }}
            // Yup validation schema - validates before form submission
            validationSchema={Yup.object({
                country_code: Yup.string().required("Vinsamlegast veldu landakóða"),
                name: Yup.string().required("Vinsamlegast sláðu inn nafn"),
            })}
            onSubmit={(values) => {
                // Trigger the purchase flow with confirmation modal and reference input
                tryPurchaseItem(values, "confirm-modal-with-reference");
            }}
        >
            {({ setFieldValue }) => (
                <Form>
                    <div className="form__body ">
                        <div className="form__section">
                            <div className="form__row">
                                <div className="form__col nationality" >
                                    {/* 
                                        Using Formik's Field component to connect the dropdown to Formik's state
                                        The Field component manages value, onChange, and validation automatically
                                    */}
                                    <Field name="country_code">
                                        {({ field, meta }: FieldProps) => (
                                            <DropSelectSearch
                                                disabled={isDisabled}
                                                size="lg"
                                                label="Landakóði"
                                                options={CountryList}
                                                defaultValue={field.value}
                                                onChange={(value) => setFieldValue("country_code", value)}
                                                error={meta.touched && meta.error ? meta.error : undefined}
                                            />
                                        )}
                                    </Field>
                                </div>
                                <div className="form__col">
                                    {/* 
                                        Using Formik's Field component for the Input
                                        This connects the input to Formik's state management
                                    */}
                                    <Field name="name">
                                        {({ field, meta }: FieldProps) => (
                                            <Input
                                                fullWidth
                                                disabled={isDisabled}
                                                label="Nafn fyrirtækis"
                                                id="name"
                                                {...field}
                                                error={meta.touched && meta.error ? meta.error : undefined}
                                            />
                                        )}
                                    </Field>
                                </div>
                                <div style={{ flexGrow: 0 }} className="form__col form__actions">
                                    <Button
                                        type="submit"
                                        buttonType="buy"
                                        showLoader={tryToBuyItem !== null}
                                        disabled={isDisabled}
                                        size="lg"
                                    >
                                        Kaupa
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Form>
            )}
        </Formik>
    );

    return (
        <>
            {/* 
                If user has access (isDisabled = false), show the enabled form
                Otherwise, wrap the disabled form in a tooltip explaining why it's disabled
            */}
            {!isDisabled ? (
                renderForm()
            ) : (
                <Tooltip
                    tooltip={<span>Eingöngu fyrir tilkynningarskylda aðila í áskrift.</span>}
                    text={renderForm()}
                />
            )}
            {/* Render any modals and toasts that are part of the buying process */}
            <ModalsAndToasts />
        </>
    );
};

export default AlthjodlegirValistarForm;